import type { TFunction } from "i18next";
import type { MissionsCampaign } from "./types";
import { missionRewardPrize, missionsTopPrize } from "./types";
import { formatButtonPrizeTitle } from "@/utils/helpers";
import { MISSIONS_HOW_IT_WORKS } from "@/components/campaigns/missions/missions-sections";

import { optInRows, registerCampaignType } from "../../registry";
import { MissionContent } from "./launcher";
import { missionView } from "./launcher-view";
import { campaignKey } from "../../campaign-key";
import { canJoinCampaign } from "../../can-join";
import type { CampaignTypeModule, CurrentCampaign } from "../../registry";
import type { AppStore } from "@/stores/app.store";
import { selectMissionsCampaignById } from "@/stores/missions.store";
import { selectJoined } from "@/stores/campaigns.store";
import {
  isActiveCampaignStatus,
  isFinishedCampaignStatus,
} from "../../campaign-status";

import type { CampaignView } from "@/components/campaigns/campaign-shell/campaign-view";
import { FINISHED_DIM } from "@/components/campaigns/campaign-shell/campaign-view";
import {
  CampaignDescriptionText,
  EligibleGamesSection,
  HowItWorksCard,
  InfoSection,
  CampaignTimeLeftChip,
} from "@/components/campaigns/campaign-shell/campaign-sections";
import {
  MissionLadder,
  MissionsCompletedCard,
  MissionsLandscapeDetail,
  MissionsLandscapeNav,
  MissionSelectionProvider,
} from "@/components/campaigns/missions/missions-sections";
import {
  CampaignJoinCta,
  CampaignJoinStickyBar,
} from "@/components/campaign-card/campaign-join-cta";
import { AtLg } from "@/components/breakpoint";
import { CampaignFinishedCard } from "@/components/campaign-card/campaign-finished-card";
import { MissionsDockPanel } from "@/components/campaigns/missions/missions-dock-panel";
import { MissionsHomeCard } from "@/components/campaigns/missions/missions-home-card";
import { FinishedMissionsCard } from "@/components/campaign-card/finished-campaign/finished-missions-card";
import { cn } from "@/lib/utils";

const missionsData = (current: CurrentCampaign): MissionsCampaign =>
  current.data as MissionsCampaign;

function detailView(
  current: CurrentCampaign,
  t: TFunction
): CampaignView | null {
  if (current.type !== "missions") return null;
  const mc = missionsData(current);
  const finished = mc.campaign.status === "FI";
  const joined = current.joined;
  // Only campaigns that require opt-in show a Join CTA ("NONE" = no opt-in).
  const canJoin = canJoinCampaign(mc.campaign, joined);
  const finishedTitle = t("missions.finished", "Campaign finished");
  const howItWorks = (
    <HowItWorksCard
      text={t("missions.howItWorks", MISSIONS_HOW_IT_WORKS)}
      minimumBet={mc.minimum_bet}
    />
  );
  return {
    campaign: mc.campaign,
    // Deliberately "Overview", not "Missions": partners brand the feature with
    // their own word, so the widget UI avoids the noun (mobile's primary tab
    // is already "Overview" — this also unifies the two).
    primaryLabel: t("nav.overview", "Overview"),
    finished,
    mobileNotice: finished ? (
      <CampaignFinishedCard campaign={mc.campaign} title={finishedTitle} />
    ) : undefined,
    sidebar: finished ? (
      <>
        <AtLg>
          <CampaignFinishedCard campaign={mc.campaign} title={finishedTitle} />
        </AtLg>
        {/* Desktop only: phones read progress off the rungs themselves, and the guide moves
            to the Info tab there. */}
        <AtLg>
          <div className={FINISHED_DIM}>{howItWorks}</div>
          <MissionsCompletedCard missionsCampaign={mc} />
        </AtLg>
      </>
    ) : (
      <>
        {canJoin && (
          <AtLg>
            <CampaignJoinCta campaign={mc.campaign} />
          </AtLg>
        )}
        <AtLg>
          {howItWorks}
          <MissionsCompletedCard missionsCampaign={mc} />
        </AtLg>
      </>
    ),
    guide: finished ? (
      <div className={FINISHED_DIM}>{howItWorks}</div>
    ) : (
      howItWorks
    ),
    primary: finished ? (
      <div className={cn("w-full", FINISHED_DIM)}>
        <MissionLadder missionsCampaign={mc} />
      </div>
    ) : (
      <MissionLadder missionsCampaign={mc} />
    ),
    // Sideways the ladder splits: the steps as a navigation column on the left (where the ladder
    // already stands in portrait), the selected step's reward and tasks in the wide pane on the
    // right. The pick is shared between the two panes through the provider in `frame`.
    landscape: {
      narrowSide: "start",
      frame: (panes) => (
        <MissionSelectionProvider>{panes}</MissionSelectionProvider>
      ),
      narrow: (
        <div className={cn("flex w-full flex-col", finished && FINISHED_DIM)}>
          <MissionsLandscapeNav missionsCampaign={mc} />
        </div>
      ),
      wide: (
        <div className={cn("flex w-full flex-col", finished && FINISHED_DIM)}>
          <MissionsLandscapeDetail missionsCampaign={mc} />
        </div>
      ),
    },
    info: (
      <div className="flex w-full flex-col gap-2 lg:gap-4">
        <InfoSection
          title={t("campaign.aboutHeading", "About")}
          aside={<CampaignTimeLeftChip campaign={mc.campaign} />}
        >
          <CampaignDescriptionText text={mc.campaign.campaign_info} />
        </InfoSection>
        <EligibleGamesSection
          games={mc.eligible_games}
          count={mc.eligible_games_count}
          campaignId={mc.campaign.id}
        />
      </div>
    ),
    canJoin,
    mobileStickyAction: canJoin ? (
      <CampaignJoinStickyBar campaign={mc.campaign} />
    ) : undefined,
  };
}

/** Missions (`campaign.type === "MI"`). */
const missionsModule: CampaignTypeModule = {
  id: "missions",
  providers: true,
  launcher: {
    // Its own content on the launcher (the prize banner, the progress bar,
    // the task pill) — `./launcher`; the ask and the paid prize are the
    // shared set's.
    Content: MissionContent,
    // Whether you opted in arrives with the progress stream: until then the
    // launcher holds the coin pending (a campaign without opt-in needn't
    // wait) — never a live bar to someone whose rounds don't count.
    pending: (current, state) =>
      current.campaign.opt_in_method !== "NONE" &&
      !(campaignKey(current.campaign) in state.joinedById),
    // An ended campaign keeps its place for its display window, finished
    // (the tournament's set).
    showsFinished: true,
    rows: (current, state, t) => {
      const mc = current.data as MissionsCampaign;
      // Finished: the faded "Finished" cell on top and, on the dark faded
      // cell under it, your result — the last step's prize you were paid,
      // in gold with a gold check, or "No win" (Sebastian, 2026-10-19: "do
      // the same for missions" as the tournament's finished state). Not
      // opted in: the cell reads "Finished" alone.
      if (isFinishedCampaignStatus(current.campaign.status)) {
        if (!current.joined)
          return { hero: t("igwButton.finished"), heroDisabled: true };
        const paid = [...mc.missions].reverse().find((m) => m.isCompleted);
        const won = paid ? missionRewardPrize(paid) : null;
        return {
          hero: won ? formatButtonPrizeTitle(won.title, t) : t("prizes.noWin"),
          ...(won && { heroKind: "prize" as const, heroWon: true }),
          heroDisabled: true,
          prizeCatalog: won?.is_catalog_item,
          top: t("igwButton.finished"),
          topKind: "status" as const,
        };
      }
      // Not opted in: the ask is the hero, the top prize its badge — the
      // tournament's set (D-101; 👺 Belmont, B1), in the panel's own words.
      if (
        canJoinCampaign(current.campaign, current.joined) &&
        campaignKey(current.campaign) in state.joinedById
      ) {
        return optInRows(current.campaign, t);
      }
      const view = missionView(
        mc,
        state.currency,
        state.missionFocusById[campaignKey(mc.campaign)]
      );
      // Every step done: "Completed" in the prize's gold on the dark chip,
      // taller, in the top prize's gleaming gold rim with a gold check seal
      // — the crown's moment, for missions (Sebastian, 2026-10-19: "or just
      // say 'completed'"). Never a bare coin (👺 Belmont, B3).
      if (!view)
        return {
          hero: t("missions.completedPill"),
          heroFaded: true,
          heroTone: "prize",
          heroCelebrate: true,
        };
      // The content's own state: these only name the control, from the view
      // the content draws — the step, the prize, the task and how far.
      return {
        hero: t("missions.stepOf", {
          current: view.step,
          total: mc.missions.length,
        }),
        top: view.prize && formatButtonPrizeTitle(view.prize, t),
        topLabel: t("igwButton.prize"),
        bottom: [
          view.task,
          view.value !== null && view.total !== null
            ? `${view.value} ${t("missions.ofTotal", { total: view.total })}`
            : null,
        ]
          .filter(Boolean)
          .join(", "),
      };
    },
  },
  resolve(state: AppStore, key): CurrentCampaign | null {
    const mc = selectMissionsCampaignById(state, key);
    if (!mc) return null;
    return {
      type: "missions",
      campaign: mc.campaign,
      joined: selectJoined(state, key),
      data: mc,
    };
  },
  listActive: (state) =>
    state.missionsCampaigns
      .filter((mc) => isActiveCampaignStatus(mc.campaign.status))
      .map((mc) => ({ campaign: mc.campaign, data: mc })),
  listFinished: (state) =>
    state.missionsCampaigns
      .filter((mc) => isFinishedCampaignStatus(mc.campaign.status))
      .map((mc) => ({ campaign: mc.campaign, data: mc })),
  hook: (entry, t) => {
    const prize = missionsTopPrize(entry.data as MissionsCampaign);
    return prize
      ? {
          label: t("campaign.topPrize", "Top prize"),
          value: formatButtonPrizeTitle(prize.title, t),
          isCatalogItem: prize.is_catalog_item,
        }
      : null;
  },
  homeCard: (entry, layout) => (
    <MissionsHomeCard
      missionsCampaign={entry.data as MissionsCampaign}
      layout={layout}
    />
  ),
  finishedCard: (entry) => (
    <FinishedMissionsCard missionsCampaign={entry.data as MissionsCampaign} />
  ),
  detailView,
  dockPanel: (current) =>
    current.type === "missions" ? (
      <MissionsDockPanel
        missionsCampaign={missionsData(current)}
        joined={current.joined}
      />
    ) : null,
};

registerCampaignType(missionsModule);
